import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { FileTableEntry, FileTableItem } from '@r1-compass-library/components/file-table/file-table.model';
import {
    AssetMetadata,
    AssetStateMessage,
    BasicFolder,
    IamDivision,
    IamUser,
    isFolder,
    Play,
    UsersIamDivisionChoice,
} from '@r1-compass-library/models';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import { AssetSearch } from '@r1-compass-library/models/search/asset-search.model';
import { BasicFolderSearch } from '@r1-compass-library/models/search/basic-folder-search.model';
import { AssetRestService } from '@r1-compass-library/services/rest/asset-rest.service';
import { FolderRestService } from '@r1-compass-library/services/rest/folder-rest.service';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { GlobalUsersStore } from '@r1-compass-library/store/users/globalUsersStore';
import { filterNil } from 'ngxtension/filter-nil';
import { Observable, combineLatest, exhaustMap, Subject } from 'rxjs';
import { exhaustOp, rxMutation } from '@angular-architects/ngrx-toolkit';
import { SwitchDivisionService } from '@r1-compass-library/services/switch-division.service';
import { defaultRowOptionActions } from '@r1-compass-library/components/file-table/file-table.constants';

export interface AssetListState<A> {
    assets: A[];
    folders: BasicFolder[];
    currentFolder: BasicFolder | null;
    dependencies?: Dependency[] | undefined;
    message: AssetStateMessage | undefined;
}

@Injectable()
export abstract class AssetListStore<
    A extends AssetMetadata,
    ARS extends AssetRestService<A, AssetSearch>,
    FRS extends FolderRestService<BasicFolder, BasicFolderSearch>,
> extends ComponentStore<AssetListState<A>> {
    readonly #globalLoggedInUserStore: GlobalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #globalUsersStore: GlobalUsersStore = inject(GlobalUsersStore);
    readonly #destroyRef = inject(DestroyRef);
    readonly #switchDivisionService = inject(SwitchDivisionService);

    protected abstract initialState: AssetListState<A>;
    protected abstract assetTypeName: string;

    assets$ = this.select(state => state.assets);
    message$ = this.select(state => state.message);
    readonly copiedAsset$ = new Subject<A>();
    readonly movedAsset$ = new Subject<A>();
    readonly movedFolder$ = new Subject<BasicFolder>();
    private folders$ = this.select(state => state.folders);
    currentFolder$ = this.select(state => state.currentFolder);

    private content$ = this.select(state => [...state.folders, ...state.assets]);
    private content = toSignal(this.content$, { initialValue: [] });

    private contentWithActions = computed<FileTableEntry[]>(() => {
        const dependencies = this.state().dependencies ?? [];
        const users = this.#globalUsersStore.users();
        const content = this.content();

        return content.map(contentItem => this.convertToFileTableEntry(contentItem, dependencies, users));
    });
    private contentWithActions$ = toObservable(this.contentWithActions);

    private userId$ = toObservable(this.#globalLoggedInUserStore.selectedUserId);
    public divisionId$ = toObservable(
        computed(() => this.#globalLoggedInUserStore.selectedDivisionConfig()?.iamDivisionId)
    );

    vm$ = this.select({
        assets: this.assets$,
        folders: this.folders$,
        currentFolder: this.currentFolder$,
        content: this.content$,
        contentWithActions: this.contentWithActions$,
        dependencies: this.select(store => store.dependencies),
    });

    vm = computed(() => {
        return {
            assets: this.state().assets,
            folders: this.state().folders,
            currentFolder: this.state().currentFolder,
            contentWithActions: this.contentWithActions(),
            dependencies: this.state().dependencies,
        };
    });

    dependenciesForPlays = computed(() => {
        const dependencies = this.state().dependencies ?? [];
        return this.vm().assets.map(asset => {
            return dependencies.filter(dependency => dependency.assetId === asset.id);
        });
    });

    usersAvailableDivisions$ = toObservable(this.#globalLoggedInUserStore.usersAvailableDivisions);
    selectedIamDivision$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivision);

    constructor(
        // eslint-disable-next-line @angular-eslint/prefer-inject
        private assetRestService: ARS,
        // eslint-disable-next-line @angular-eslint/prefer-inject
        private folderRestService: FRS
    ) {
        super({ assets: [], folders: [], currentFolder: null, dependencies: undefined, message: undefined });
        this.precheckForUsersStore();
    }

    reset = this.updater(state => ({
        ...this.initialState,
    }));

    protected setFolders = this.updater((state, folders: BasicFolder[]) => ({
        ...state,
        folders: folders,
    }));
    private addFolder = this.updater((state, newFolder: BasicFolder) => ({
        ...state,
        folders: [...state.folders, newFolder],
    }));
    private updateFolder = this.updater((state, updatedFolder: BasicFolder) => ({
        ...state,
        folders: state.folders.map(folder => {
            if (updatedFolder.id === folder.id) {
                return updatedFolder;
            }
            return folder;
        }),
    }));
    private setCurrentFolder = this.updater((state, folder: BasicFolder) => ({
        ...state,
        currentFolder: folder,
    }));

    protected setAssets = this.updater((state, assets: A[]) => ({
        ...state,
        assets: assets,
    }));
    private removeAsset = this.updater((state, id: number) => ({
        ...state,
        assets: state.assets.filter(asset => asset.id !== id),
    }));

    protected setDependency = this.updater((state, newDep: Dependency) => {
        return {
            ...state,
            dependencies: [...(state.dependencies ?? []), newDep],
        };
    });

    protected setMessage = this.updater((state, message: AssetStateMessage) => ({
        ...state,
        isLoading: false,
        message: message,
    }));

    resetMessage = this.updater(state => ({
        ...state,
        isLoading: false,
        message: undefined,
    }));

    protected setDependencies = this.updater((state, newDependencies: Dependency[]) => {
        const dependencies = [...(state.dependencies ?? [])];
        newDependencies.forEach(newDependency => {
            if (
                !dependencies.find(
                    dependency =>
                        newDependency.assetId === dependency.assetId && newDependency.playId === dependency.playId
                )
            ) {
                dependencies.push(newDependency);
            }
        });
        return {
            ...state,
            dependencies: dependencies,
        };
    });

    /** Method used to prevent Dependencies from getting repeatedly added until full refresh, leading to incorrect display
     * of values after extended use.
     * Set to "undefined" to allow for 'Loading...' message to appear on page changes
     * */
    protected clearDependencies = this.updater(state => {
        return {
            ...state,
            dependencies: undefined,
        };
    });

    /** Sort of a fallback method for if an entire list of Assets have no dependencies, since the forEach loop
     * won't run in the case of them being empty */
    protected setBlankDependencies = this.updater(state => {
        return {
            ...state,
            dependencies: [...(state.dependencies ?? [])],
        };
    });

    public getAssets = rxMutation<AssetSearch, A[]>({
        operation: (search: AssetSearch) => {
            return this.assetRestService.search(search);
        },
        operator: exhaustOp,
        onSuccess: assets => {
            this.setAssets(assets);
        },
        onError: err => console.error(err),
    });

    getAssetsAndFolders = rxMutation({
        operation: (args: {
            folderId: number;
            usersAvailableDivisions: UsersIamDivisionChoice[];
            selectedIamDivision: IamDivision | undefined;
        }) => {
            const userDivisionCodes: string[] = args.usersAvailableDivisions.map(
                availableDivision => availableDivision.divisionCode
            );
            return combineLatest([
                this.assetRestService.search({ folderId: args.folderId, divisions: userDivisionCodes }),
                this.folderRestService.search({ parentFolderId: args.folderId, divisions: userDivisionCodes }),
            ]);
        },
        operator: exhaustOp,
        onSuccess: ([assets, folders], args) => {
            if (assets.length > 0) {
                const retrievedAsset = assets[0];
                this.switchDivisionCheck(
                    retrievedAsset.iamDivisionId,
                    args.selectedIamDivision?.id,
                    args.usersAvailableDivisions
                );
            }

            this.handleDependencies(assets);

            this.setAssets(assets);
            this.setFolders(folders);
        },
    });

    getAssetsAndFoldersInRoot = rxMutation({
        operation: (args: { rootFolder: boolean; divisionId: number | undefined }) => {
            return combineLatest([
                this.assetRestService.search({ rootFolder: args.rootFolder, iamDivisionId: args.divisionId }),
                this.folderRestService.search({
                    rootFolder: args.rootFolder,
                    iamDivisionId: args.divisionId,
                }),
            ]).pipe();
        },
        operator: exhaustOp,
        onSuccess: ([files, folders]) => {
            this.handleDependencies(files);

            this.setAssets(files);
            this.setFolders(folders);
        },
    });

    copyAsset = this.effect((trigger$: Observable<number | Partial<A> | FileTableItem>) => {
        return combineLatest([trigger$, this.userId$]).pipe(
            exhaustMap(([trigger, userId]) => {
                const assetId = typeof trigger === 'number' ? trigger : (trigger.id as number);
                return this.assetRestService.copy(assetId, userId).pipe(
                    tapResponse({
                        next: result => {
                            if (result?.copiedAsset) {
                                const currentFolderId = this.state().currentFolder?.id ?? null;
                                this.reloadAssetsAndFolder(currentFolderId);
                                this.copiedAsset$.next(result.copiedAsset);
                            }

                            if (result?.issues && result?.issues.length) {
                                const formattedMessage = result.issues
                                    .map(issue => {
                                        const details = issue.details?.length ? issue.details.join(', ') : '';
                                        return `${issue.message} Images: ${details}`;
                                    })
                                    .join(' ');

                                this.setMessage({
                                    type: 'Error',
                                    message: formattedMessage,
                                    statusCode: 404,
                                });
                            }
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    moveAsset = this.effect((trigger$: Observable<{ asset: A; destinationFolderId: number | null }>) => {
        return trigger$.pipe(
            filterNil(),
            exhaustMap(({ asset, destinationFolderId }) => {
                const originFolderId = asset.folderId ?? this.state().currentFolder?.id ?? null;
                return this.assetRestService.move(asset, destinationFolderId).pipe(
                    tapResponse({
                        next: movedAsset => {
                            this.reloadAssetsAndFolder(originFolderId);
                            this.movedAsset$.next(movedAsset);
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    deleteAsset = this.effect((trigger$: Observable<number>) => {
        return trigger$.pipe(
            filterNil(),
            exhaustMap(id => {
                return this.assetRestService.delete(id).pipe(
                    tapResponse({
                        next: () => this.removeAsset(id),
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    moveFolder = this.effect((trigger$: Observable<{ folder: BasicFolder; destinationFolderId: number }>) => {
        return trigger$.pipe(
            filterNil(),
            exhaustMap(({ folder, destinationFolderId }) => {
                const originFolderId = this.state().currentFolder?.id ?? null;
                folder.parentFolderId = destinationFolderId;
                return this.folderRestService.update(folder.id, folder).pipe(
                    tapResponse({
                        next: updatedFolder => {
                            this.reloadAssetsAndFolder(originFolderId);
                            this.movedFolder$.next(updatedFolder);
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    searchAssets = this.effect((search$: Observable<AssetSearch>) => {
        return search$.pipe(
            exhaustMap(search => {
                return this.assetRestService.search(search).pipe(
                    tapResponse({
                        next: templates => this.setAssets(templates),
                        error: (err: HttpErrorResponse) => console.log(err),
                    })
                );
            })
        );
    });

    saveAssetFolder = this.effect((folder$: Observable<Partial<BasicFolder>>) => {
        return folder$.pipe(
            filterNil(),
            exhaustMap(folder => {
                if (folder.id) {
                    return this.saveUpdatedFolder(folder.id, folder);
                } else {
                    return this.saveNewFolder(folder);
                }
            })
        );
    });

    getCurrentFolder = rxMutation({
        operation: (args: {
            folderId: number;
            usersAvailableDivisions: UsersIamDivisionChoice[];
            selectedIamDivision: IamDivision;
        }) => {
            const userDivisionCodes: string[] = args.usersAvailableDivisions.map(
                availableDivision => availableDivision.divisionCode
            );
            return this.folderRestService.search({ id: args.folderId, divisions: userDivisionCodes });
        },
        operator: exhaustOp,
        onSuccess: (folders, args) => {
            if (folders.length > 0) {
                const retrievedFolder = folders[0];

                this.switchDivisionCheck(
                    retrievedFolder.iamDivisionId,
                    args.selectedIamDivision.id,
                    args.usersAvailableDivisions
                );

                this.setCurrentFolder(retrievedFolder);
                return folders;
            } else {
                // no match found for any division, they don't have access/it doesn't exist
                this.setMessage({
                    type: 'Error',
                    message: `No ${this.assetTypeName} found or user doesn't have access.`,
                });
                return [];
            }
        },
    });

    protected abstract handleDependencies(assets: A[]): void;

    protected abstract buildDependencies(play: Play, assets: A[]): Dependency[];

    protected setDependenciesForAssets(plays2dArray: Play[][], assets: A[]) {
        plays2dArray.forEach(playArray => {
            if (playArray.length === 0) {
                this.setBlankDependencies();
            } else {
                playArray.forEach(play => {
                    const dependencies = this.buildDependencies(play, assets);
                    this.setDependencies(dependencies);
                });
            }
        });
    }

    private reloadAssetsAndFolder(folderId: number | null) {
        if (folderId && folderId !== 0) {
            this.getAssetsAndFolders({
                folderId: folderId,
                usersAvailableDivisions: this.#globalLoggedInUserStore.usersAvailableDivisions(),
                selectedIamDivision: this.#globalLoggedInUserStore.selectedIamDivision(),
            });
        } else {
            this.getAssetsAndFoldersInRoot({
                rootFolder: true,
                divisionId: this.#globalLoggedInUserStore.selectedDivisionConfig()?.iamDivisionId,
            });
        }
    }

    private saveUpdatedFolder(id: number, folder: Partial<BasicFolder>) {
        return this.folderRestService.update(id, folder).pipe(
            tapResponse({
                next: folderRes => {
                    this.updateFolder(folderRes);
                },
                error: (err: HttpErrorResponse) => console.error(err),
            })
        );
    }

    private saveNewFolder(folder: Partial<BasicFolder>) {
        return this.folderRestService.create(folder).pipe(
            tapResponse({
                next: folderRes => {
                    this.addFolder(folderRes);
                },
                error: (err: HttpErrorResponse) => console.error(err),
            })
        );
    }

    /**
     * If the users store is empty, load the users.
     */
    private precheckForUsersStore() {
        if (!this.#globalUsersStore.compactDivisionUsers().length) {
            this.#globalUsersStore.getCompactDivisionUsers();
        }
    }

    private convertToFileTableEntry(
        item: BasicFolder | AssetMetadata,
        dependencies: Dependency[],
        users: IamUser[]
    ): FileTableEntry {
        const itemType = isFolder(item) ? 'folder' : 'asset';
        const fileTableItem: FileTableItem = {
            ...item,
            dependencyCount:
                itemType === 'asset' ? dependencies?.filter(dep => dep.assetId === item.id).length : undefined,
            updatedByUsername: users.find(user => user.id === item.updatedBy)?.username ?? undefined,
            createdByUsername: users.find(user => user.id === item.createdBy)?.username ?? undefined,
        };
        // display the last modified in UI date instead of last updated date (if present).
        if (fileTableItem.lastConfigured) {
            fileTableItem.updatedAt = fileTableItem.lastConfigured;
        }
        return {
            itemType: isFolder(item) ? 'folder' : 'asset',
            actions: defaultRowOptionActions[itemType],
            item: fileTableItem,
            ...fileTableItem,
        };
    }

    private switchDivisionCheck(
        retrievedIamDivisionId: number,
        selectedIamDivisionId: number | undefined,
        usersAvailableDivisions: UsersIamDivisionChoice[]
    ) {
        if (retrievedIamDivisionId !== selectedIamDivisionId) {
            const divisionCode =
                usersAvailableDivisions.find(division => division.id === retrievedIamDivisionId)?.divisionCode ??
                'Unknown';
            this.#switchDivisionService
                .switchDivision(retrievedIamDivisionId, divisionCode)
                .pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe(() => {
                    this.setMessage({
                        type: 'Info',
                        message: `Switched to ${divisionCode} division.`,
                    });
                });
        }
    }
}
